import { useState } from "react";

import { Button, Icon, Portal } from "@blueprintjs/core";
import { useInView } from "react-intersection-observer";
import { Link, useLocation, useNavigate } from "react-router";

import { Tooltip } from "@/components/helpers/ui/blueprint-overrides/Tooltip";
import { buildUrl } from "@/components/helpers/url-utils";
import { Box, Flex } from "@/components/layout/flexbox";
import { Media } from "@/components/layout/Media";
import { BackDrop } from "@/components/pieces/BackDrop";
import { Overlay } from "@/components/pieces/overlay/Overlay";

interface LearnQuickViewLinkProps {
  link: string;
  children: React.ReactNode;
}

export function LearnQuickViewLink({
  link,
  children,
}: LearnQuickViewLinkProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const [overlayVisible, setOverlayVisible] = useState(false);
  const [ref, inView] = useInView({
    triggerOnce: true,
  });

  const hash = link.split("#")[1];
  const baseLink = link.split("#")[0];
  const url = buildUrl(baseLink, {
    hash,
  });
  const iframeUrl = buildUrl(baseLink, {
    data: {
      layout: "bare",
    },
    hash,
  });

  const close = () => {
    setOverlayVisible(false);
    window.history.replaceState(null, "", location.pathname + location.search);
  };

  const open = () => {
    setOverlayVisible(true);
    window.history.replaceState(null, "", url);
  };

  function handleClick(event: React.MouseEvent<HTMLAnchorElement>) {
    if (!(event.metaKey || event.altKey || event.ctrlKey || event.shiftKey)) {
      event.preventDefault();
      open();
    }
  }

  return (
    <>
      <Media greaterThan="xs">
        {(className, renderChildren) =>
          renderChildren && (
            <a ref={ref} href={url} onClick={handleClick} className={className}>
              {children}
            </a>
          )
        }
      </Media>
      <Media at="xs">
        {(className, renderChildren) =>
          renderChildren && (
            <Link to={url} style={{ width: "100%" }} className={className}>
              {children}
            </Link>
          )
        }
      </Media>

      {inView ? (
        <Portal>
          <BackDrop visible={overlayVisible} onClick={close} opacity={0.7} />
          <Overlay
            visible={overlayVisible}
            top={30}
            cardWidth={1500}
            style={{
              maxWidth: "92%",
              paddingBottom: 30,
            }}
            onClose={close}
          >
            <Box
              style={{
                position: "relative",
              }}
              height="100%"
            >
              <Flex
                style={{ position: "absolute", top: 8, right: 8 }}
                alignItems="center"
              >
                <Tooltip content="Open as page" placement="bottom">
                  <Link
                    to={url}
                    onClick={(e) => {
                      e.preventDefault();
                      window.history.replaceState(
                        null,
                        "",
                        location.pathname + location.search,
                      );
                      navigate(url);
                    }}
                  >
                    <Button
                      minimal={true}
                      icon={<Icon icon="maximize" size={20} />}
                    />
                  </Link>
                </Tooltip>
                <Tooltip content="Close" placement="bottom">
                  <Button
                    minimal={true}
                    icon={<Icon icon="cross" size={24} />}
                    onClick={close}
                  />
                </Tooltip>
              </Flex>
              <iframe
                src={iframeUrl}
                style={{
                  width: "100%",
                  height: "100%",
                  borderRadius: 6,
                  border: 0,
                }}
              />
            </Box>
          </Overlay>
        </Portal>
      ) : null}
    </>
  );
}
